/**
 * ALOHANOVA UNIVERSE - Three.js Supernova Background
 * Copyright © 2025 Alohanova Universe, Malcolm KH Lee III. All Rights Reserved.
 * 
 * 60-Second Loop with Supernova at 55s
 */

import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const SupernovaBackground = ({ effectsOn = true, resolution = 'auto' }) => {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const starsRef = useRef(null);
  const particlesRef = useRef(null);
  const supernovaRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());

  useEffect(() => {
    if (!canvasRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 2;
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    setResolution(resolution, renderer);
    rendererRef.current = renderer;

    // Background Stars (10,000 static stars)
    const starGeometry = new THREE.BufferGeometry();
    const starVertices = [];
    for (let i = 0; i < 10000; i++) {
      const x = (Math.random() - 0.5) * 2000;
      const y = (Math.random() - 0.5) * 2000;
      const z = (Math.random() - 0.5) * 2000;
      starVertices.push(x, y, z);
    }
    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.7
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);
    starsRef.current = stars;

    // Foreground Particles (500 moving gold particles)
    const particleGeometry = new THREE.BufferGeometry();
    const particleVertices = [];
    for (let i = 0; i < 500; i++) {
      const x = (Math.random() - 0.5) * 10;
      const y = (Math.random() - 0.5) * 10;
      const z = (Math.random() - 0.5) * 10;
      particleVertices.push(x, y, z);
    }
    particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(particleVertices, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffd700, // Gold
      size: 0.05,
      transparent: true,
      opacity: 0.8
    });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);
    particlesRef.current = particles;

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      const elapsedTime = clockRef.current.getElapsedTime();
      const loopTime = elapsedTime % 60; // 60-second loop

      if (effectsOn) {
        // Animate background stars (slow rotation)
        if (starsRef.current) {
          starsRef.current.rotation.y += 0.0001;
        }

        // Animate foreground particles (faster)
        if (particlesRef.current) {
          particlesRef.current.rotation.y += 0.0005;
          
          // Move particles forward
          const positions = particlesRef.current.geometry.attributes.position.array;
          for (let i = 0; i < positions.length; i += 3) {
            positions[i + 2] += 0.01; // Z-axis
            if (positions[i + 2] > 5) {
              positions[i + 2] = -5; // Reset
            }
          }
          particlesRef.current.geometry.attributes.position.needsUpdate = true;
        }
      }

      // SUPERNOVA EVENT (55-60 seconds)
      if (loopTime >= 55 && !supernovaRef.current) {
        createSupernova(scene);
      }

      if (supernovaRef.current) {
        const supernovaTime = loopTime - 55;
        animateSupernova(supernovaTime);
      }

      // Remove supernova when loop resets
      if (loopTime < 1 && supernovaRef.current) {
        removeSupernova(scene);
      }

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, [effectsOn, resolution]);

  const createSupernova = (scene) => {
    if (supernovaRef.current) return;

    const particleCount = 20000;
    const positions = new Float32Array(particleCount * 3);

    // Particles start at center
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xff8800, // Fiery orange
      size: 0.08,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending
    });

    const supernova = new THREE.Points(geometry, material);
    
    // Random position in distance
    supernova.position.x = (Math.random() - 0.5) * 50;
    supernova.position.y = (Math.random() - 0.5) * 50;
    supernova.position.z = -Math.random() * 100 - 50;

    scene.add(supernova);
    supernovaRef.current = supernova;
  };

  const animateSupernova = (supernovaTime) => {
    if (!supernovaRef.current) return;

    // Expand (0-5 seconds)
    const expansionFactor = 1.0 + (supernovaTime * 15);
    supernovaRef.current.scale.set(expansionFactor, expansionFactor, expansionFactor);

    // Fade out after 1 second
    const opacity = Math.max(0, 1.0 - (supernovaTime - 1) / 4.0);
    supernovaRef.current.material.opacity = opacity;
  };

  const removeSupernova = (scene) => {
    if (!supernovaRef.current) return;

    scene.remove(supernovaRef.current);
    supernovaRef.current.geometry.dispose();
    supernovaRef.current.material.dispose();
    supernovaRef.current = null;
  };

  const setResolution = (res, renderer) => {
    if (!renderer) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let pixelRatio = window.devicePixelRatio;

    switch (res) {
      case '480':
        width = 854; height = 480; pixelRatio = 1;
        break;
      case '720':
        width = 1280; height = 720; pixelRatio = 1;
        break;
      case '1080':
        width = 1920; height = 1080; pixelRatio = 1;
        break;
      case '4k':
        width = 3840; height = 2160; pixelRatio = 1;
        break;
      case '8k':
        width = 7680; height = 4320; pixelRatio = 1;
        break;
      case 'auto':
      default:
        width = window.innerWidth;
        height = window.innerHeight;
        pixelRatio = window.devicePixelRatio;
        break;
    }

    if (res === 'auto') {
      renderer.setSize(width, height);
      renderer.setPixelRatio(pixelRatio);
    } else {
      renderer.setSize(width, height);
      renderer.setPixelRatio(1);
      renderer.domElement.style.width = '100vw';
      renderer.domElement.style.height = '100vh';
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (cameraRef.current && rendererRef.current) {
        cameraRef.current.aspect = window.innerWidth / window.innerHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full -z-10"
      style={{ background: '#000' }}
    />
  );
};

export default SupernovaBackground;
